import type { JSX } from 'react';

import { Translate, TranslatePlural } from '@change-corgi/core/react/i18n';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import {
	iconArrowUpward,
	iconControlPointDuplicate,
	iconSupervisorAccount,
	iconVisibility,
} from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { InfoTooltip } from 'src/app/shared/components/tooltips';

import { usePromotionHistory } from './hooks/usePromotionHistory';

export function PromotionHistory(): JSX.Element {
	const { data } = usePromotionHistory();

	return (
		<Box p="24px">
			<Flex sx={{ alignItems: 'center', gap: 4 }}>
				<Heading id="petition-insights-title" as="h3" size="h4">
					<Translate value="corgi.petition.starter_dashboard.petition_insights.promotion_history.title" />
				</Heading>
				<InfoTooltip aria-describedby="petition-insights-title">
					<Translate
						value={`corgi.petition.starter_dashboard.petition_insights.promotion_history.tip${
							data.shouldUseDonationLanguage ? '_donation' : ''
						}`}
					/>
				</InfoTooltip>
			</Flex>

			<Flex
				sx={{
					flexDirection: ['column', 'row'],
					flexWrap: ['nowrap', 'wrap'],
					gap: [16, 24],
					mt: 24,
				}}
			>
				<Flex sx={{ alignItems: 'center' }}>
					<Icon mr="8px" size={24} icon={iconArrowUpward} color="secondary-purple" />
					<Text as="span">
						<TranslatePlural
							value="corgi.petition.starter_dashboard.petition_insights.promotion_history.promoter"
							replacements={{ promoterCount: data.promoterCountFormatted }}
							count={data.promoterCount}
						/>
					</Text>
				</Flex>
				<Flex sx={{ alignItems: 'center' }}>
					<Icon mr="8px" size={24} icon={iconControlPointDuplicate} color="secondary-purple" />
					<Text as="span">
						<Translate
							value="corgi.petition.starter_dashboard.petition_insights.promotion_history.contributions"
							replacements={{ contributionsFormatted: data.contributionsFormatted }}
						/>
					</Text>
				</Flex>
				<Flex sx={{ alignItems: 'center' }}>
					<Icon mr="8px" size={24} icon={iconVisibility} color="secondary-purple" />
					<Text as="span">
						<TranslatePlural
							value="corgi.petition.starter_dashboard.petition_insights.promotion_history.view"
							replacements={{ impressionsServedFormatted: data.impressionsTotalFormatted }}
							count={data.impressionsTotal}
						/>
					</Text>
				</Flex>
				<Flex sx={{ alignItems: 'center' }}>
					<Icon mr="8px" size={24} icon={iconSupervisorAccount} color="secondary-purple" />
					<Text as="span">
						<TranslatePlural
							value="corgi.petition.starter_dashboard.petition_insights.promotion_history.signature"
							replacements={{ signatureCountFormatted: data.signatureCountFormatted }}
							count={data.signatureCount}
						/>
					</Text>
				</Flex>
			</Flex>
		</Box>
	);
}
